import React, { FC, useCallback, useState } from 'react';

import { Icon, Image, Text } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import { useIntl } from 'react-intl';

import ActionButton from 'components/action-button';
import { CardModal } from 'components/card-modal';
import { useCardNumber } from 'components/gift-card-modal/use-giftcard-number';
import { CurrentSelectedMethodType } from 'components/payment-method/types';
import useErrorModal from 'hooks/use-error-modal';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { primitive } from 'styles/constants/primitives';
import logger from 'utils/logger';

import { AddGiftCardInput } from '../../add-gift-card-input';

import { BackgroundAndLogoContainer, GiftCardContainer, LogoAndNameContainer } from './styled';

const crownsImg = require('./Frame.png');

export const AddGiftCardRedesign: FC = () => {
  const { formatMessage } = useIntl();
  const {
    getPrepaidPaymentMethod,
    setCheckoutPaymentMethodId,
    setDefaultPaymentMethodId,
    mergePrepaidCardBalances,
  } = usePaymentContext();
  const {
    giftCardNumber,
    errors,
    handleCardNumberChange,
    isValidGiftCardNumberCheck,
    validateCardNumber,
  } = useCardNumber();
  // @ts-ignore
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Vaulting Gift Card',
  });

  const router = useRouter();

  const { loading: loyaltyUserLoading } = useLoyaltyUserId();
  const [isCardLoading, setIsCardLoading] = useState(false);

  const addNewCard = useCallback(
    async (newGiftCardNumber: string) => {
      setIsCardLoading(true);

      if (getPrepaidPaymentMethod()) {
        setIsCardLoading(false);
        return openErrorDialog({ message: formatMessage({ id: 'giftCardExists' }) });
      }

      try {
        if (!isCardLoading && !errors.giftCardNumber && newGiftCardNumber) {
          await mergePrepaidCardBalances({
            // Source pin is not required, BK crown cards don't have it
            sourceCardNumber: newGiftCardNumber,
          });

          setCheckoutPaymentMethodId(CurrentSelectedMethodType.ADD_NEW_GIFT_CARD);
          setDefaultPaymentMethodId(CurrentSelectedMethodType.ADD_NEW_GIFT_CARD);
          router.dismiss();
        }
      } catch (error) {
        // @ts-expect-error TS(2322) FIXME: Type 'unknown' is not assignable to type 'Error | ... Remove this comment to see the full error message
        openErrorDialog({ message: formatMessage({ id: 'giftCardAddingError' }), error });
        logger.error({ error, message: 'Error adding gift card' });
      } finally {
        setIsCardLoading(false);
      }
    },
    [
      getPrepaidPaymentMethod,
      openErrorDialog,
      formatMessage,
      isCardLoading,
      errors.giftCardNumber,
      mergePrepaidCardBalances,
      setCheckoutPaymentMethodId,
      setDefaultPaymentMethodId,
      router,
    ]
  );

  const handleGiftCardChange = useCallback(() => {
    setIsCardLoading(true);
    handleCardNumberChange(giftCardNumber);
    validateCardNumber(giftCardNumber);

    if (isValidGiftCardNumberCheck(giftCardNumber)) {
      addNewCard(giftCardNumber?.replace(/\s+/g, ''));
    } else if (errors) {
      setIsCardLoading(false);
    }
  }, [
    addNewCard,
    errors,
    giftCardNumber,
    handleCardNumberChange,
    isValidGiftCardNumberCheck,
    validateCardNumber,
  ]);

  return (
    <CardModal
      standardPadding
      headerText={formatMessage({ id: 'addGiftCard' })}
      footer={
        <ActionButton
          fullWidth
          isLoading={loyaltyUserLoading || isCardLoading}
          onPress={handleGiftCardChange}
        >
          {formatMessage({ id: 'addToCart' })}
        </ActionButton>
      }
    >
      <GiftCardContainer>
        <BackgroundAndLogoContainer>
          <LogoAndNameContainer>
            <Icon accessibilityHidden variant="giftCard" size="xl" mb="$1" />
            <Text
              accessibilityHidden
              color={primitive.bk.$bbqBrown}
              lineHeight="24px"
              fontWeight="bold"
              fontSize="2xl"
            >
              {formatMessage({ id: 'giftCard' })}
            </Text>
          </LogoAndNameContainer>
          <Image accessibilityHidden source={crownsImg} width={162} height={145} marginTop="$7" />
        </BackgroundAndLogoContainer>
      </GiftCardContainer>
      <AddGiftCardInput
        onChange={handleCardNumberChange}
        errorMessage={errors?.giftCardNumber}
        label={formatMessage({ id: 'cardNumber' })}
        testID="giftcard-input"
        isRedesign={true}
      />
      <ErrorDialog />
    </CardModal>
  );
};
